import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { wineClubApi } from '../api/wineClub'
import { fmtFecha, fmtMonto } from '../utils/fecha'
import { useAutoRefresh } from '../hooks/useAutoRefresh'
import { useStoredState } from '../hooks/useStoredState'
import { colors, font, text, space, radius, sx } from '../design/tokens'
import Boton from '../components/Boton'
import Modal from '../components/Modal'
import BuscadorCliente from '../components/BuscadorCliente'
import CampoFecha from '../components/CampoFecha'

/**
 * CLIENTES → Wine Club — los socios y sus membresías, gestionados desde LGR y
 * replicados en Mobbex (pedido de Martin, 30 ago 2026):
 *  1. ver los socios con su nivel, estado, tarjeta y próximo cobro;
 *  2. pausar / reactivar / cambiar de nivel a cada uno (mismo servicio que
 *     usa el socio en Mi cuenta: el move conserva tarjeta y fecha de cobro,
 *     el nivel nuevo rige desde el próximo ciclo);
 *  3. ajustar el precio de cada nivel — vive en la suscripción de Mobbex.
 * La tabla solo muestra; TODAS las acciones viven en el detalle del socio
 * (click en la fila), resguardadas detrás de una confirmación (Martin, 30 ago).
 */

const ESTADOS = [
  { id: 'activos', label: 'Activos' },
  { id: 'pausados', label: 'Pausados' },
  { id: 'todos', label: 'Todos' },
]

const ESTADO_BADGE = {
  active: { label: 'Activa', bg: colors.okBg, color: colors.ok },
  suspended: { label: 'Pausada', bg: colors.warnBg, color: colors.warn },
  cancelled: { label: 'Pausada', bg: colors.warnBg, color: colors.warn },
  deleted: { label: 'Borrada', bg: colors.errBg, color: colors.err },
  finished: { label: 'Finalizada', bg: colors.bgSoft, color: colors.textSec },
}

// Por qué Mobbex no pudo cobrar. Viene en inglés y en snake_case; como todo
// estado externo en LGR, se muestra traducido y el crudo queda en el title.
const MOTIVO_MOBBEX = {
  error_expired_sources: 'Tarjeta vencida',
  rejected: 'Rechazado',
  insufficient_funds: 'Sin fondos',
  error_payment: 'Error de pago',
  failed: 'Falló',
}

const FECHAS_VACIAS = { ultimo_desde: '', ultimo_hasta: '', proximo_desde: '', proximo_hasta: '' }

function RangoFecha({ label, desde, hasta, onDesde, onHasta }) {
  return (
    <div style={st.rango}>
      <span style={st.rangoLabel}>{label}</span>
      <CampoFecha value={desde} onChange={onDesde} aria-label={`${label} desde`} style={st.campoFecha} />
      <span style={st.rangoSep}>–</span>
      <CampoFecha value={hasta} onChange={onHasta} aria-label={`${label} hasta`} style={st.campoFecha} />
    </div>
  )
}

const ESTADO_COBRO = {
  pendiente: { label: 'Pendiente', bg: colors.warnBg, color: colors.warn },
  resuelto: { label: 'Resuelto', bg: colors.bgSoft, color: colors.textSec },
}

export default function WineClubSociosPage() {
  const navigate = useNavigate()
  const [estado, setEstado] = useStoredState('lgr.wineclub.estado', 'activos')
  const [nivel, setNivel] = useStoredState('lgr.wineclub.nivel', '')
  const [pasarela, setPasarela] = useStoredState('lgr.wineclub.pasarela', '')
  const [q, setQ] = useState('')
  // Rangos de último y próximo cobro (sugerencia #119, Brenda, 23 oct 2026).
  const [fechas, setFechas] = useState(FECHAS_VACIAS)
  const hayFechas = Object.values(fechas).some(Boolean)
  const [datos, setDatos] = useState({ socios: [], resumen: null, niveles: [] })
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState(null)
  const [abierto, setAbierto] = useState(null)   // socio en el modal
  const [altaOffline, setAltaOffline] = useState(false)
  const [precios, setPrecios] = useState(null)   // niveles con lo que dice Mobbex

  const cargar = useCallback(async (silencioso = false) => {
    if (!silencioso) setCargando(true)
    try {
      const rango = Object.fromEntries(Object.entries(fechas).filter(([, v]) => v))
      const { data } = await wineClubApi.socios({ estado, nivel: nivel || undefined, proveedor: pasarela || undefined, q: q || undefined, ...rango })
      setDatos(data); setError(null)
    } catch (e) {
      setError(e.response?.data?.error || 'No se pudieron cargar los socios')
    } finally { setCargando(false) }
  }, [estado, nivel, pasarela, q, fechas])

  useEffect(() => { cargar() }, [cargar])
  useAutoRefresh(() => cargar(true), 30000, abierto === null)

  const cargarPrecios = useCallback(async () => {
    try { const { data } = await wineClubApi.niveles(); setPrecios(data.niveles) } catch { setPrecios([]) }
  }, [])
  useEffect(() => { cargarPrecios() }, [cargarPrecios])

  const socios = datos.socios
  const resumen = datos.resumen
  const niveles = datos.niveles

  // Al cerrar el modal con un cambio, la fila se refresca desde el servidor.
  const alCambiar = (socioActualizado) => {
    if (socioActualizado) setAbierto(a => (a ? { ...a, ...socioActualizado } : a))
    cargar(true)
  }

  return (
    <div>
      <div style={st.headerRow}>
        <div>
          <h1 style={st.title}>Wine Club</h1>
          <p style={st.subtitle}>
            Socios y membresías. Lo que se hace acá se hace en Mobbex: pausar, reactivar, cambiar de nivel y el precio de cada nivel.
            Los socios <strong>offline</strong> (pagan por fuera) viven solo en LGR: en su fecha, el sistema arma la NV del mes para que la procese un vendedor.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.lg }}>
          <Boton variante="primary" onClick={() => setAltaOffline(true)}>＋ Socio offline</Boton>
          {resumen && (
            <div style={st.kpis}>
              <Kpi n={resumen.activos} label="activos" />
              <Kpi n={resumen.pausados} label="pausados" />
            </div>
          )}
        </div>
      </div>

      <PreciosNiveles niveles={precios} resumen={resumen} onCambio={cargarPrecios} />

      <div style={sx.card}>
        <div style={st.toolbar}>
          <div style={st.chips}>
            {ESTADOS.map(e => (
              <button key={e.id} type="button" onClick={() => setEstado(e.id)} style={{ ...st.chip, ...(estado === e.id ? st.chipOn : null) }}>
                {e.label}
              </button>
            ))}
          </div>
          <select value={nivel} onChange={e => setNivel(e.target.value)} style={st.select}>
            <option value="">Todos los niveles</option>
            {niveles.map(n => <option key={n.nivel} value={n.nivel}>{n.nombre}</option>)}
          </select>
          <select value={pasarela} onChange={e => setPasarela(e.target.value)} style={st.select}>
            <option value="">Todas las pasarelas</option>
            <option value="mobbex">Mobbex</option>
            <option value="mp">Mercado Pago</option>
            <option value="offline">Offline</option>
            <option value="manual">A pedido (manual)</option>
          </select>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o email…" style={st.search} />
          {(estado !== 'activos' || nivel || pasarela || q || hayFechas) && (
            <Boton variante="ghost" sm onClick={() => { setEstado('activos'); setNivel(''); setPasarela(''); setQ(''); setFechas(FECHAS_VACIAS) }}>Limpiar</Boton>
          )}
          <span style={st.metaTxt}>{socios.length} socio{socios.length === 1 ? '' : 's'}</span>
        </div>

        <div style={st.toolbarFechas}>
          <RangoFecha label="Último cobro" desde={fechas.ultimo_desde} hasta={fechas.ultimo_hasta}
            onDesde={v => setFechas(f => ({ ...f, ultimo_desde: v }))} onHasta={v => setFechas(f => ({ ...f, ultimo_hasta: v }))} />
          <RangoFecha label="Próximo cobro" desde={fechas.proximo_desde} hasta={fechas.proximo_hasta}
            onDesde={v => setFechas(f => ({ ...f, proximo_desde: v }))} onHasta={v => setFechas(f => ({ ...f, proximo_hasta: v }))} />
          {(fechas.proximo_desde || fechas.proximo_hasta) && (
            <span style={st.metaTxt}>El próximo cobro solo existe en las membresías activas.</span>
          )}
        </div>

        {error && <div style={st.errTxt}>{error}</div>}

        <div style={{ overflowX: 'auto' }}>
          <table style={sx.table}>
            <thead>
              <tr>
                <th style={sx.th}>Socio</th>
                <th style={sx.th}>Nivel</th>
                <th style={sx.th}>Pasarela</th>
                <th style={sx.th}>Estado</th>
                <th style={sx.th}>Tarjeta</th>
                <th style={sx.th}>Ciclos</th>
                <th style={sx.th}>Últ. cobro</th>
                <th style={sx.th}>Próx. cobro</th>
                <th style={sx.th}>Alta</th>
              </tr>
            </thead>
            <tbody>
              {!cargando && socios.length === 0 && (
                <tr><td colSpan={9} style={st.empty}>Sin socios con esos filtros.</td></tr>
              )}
              {socios.map(s => (
                <FilaSocio key={s.id} s={s} onAbrir={() => setAbierto(s)} onCambio={alCambiar} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {abierto && (
        <SocioModal socio={abierto} niveles={niveles} onClose={() => setAbierto(null)} onCambio={alCambiar} navigate={navigate} />
      )}
      {altaOffline && (
        <AltaOfflineModal niveles={niveles} precios={precios} onClose={() => setAltaOffline(false)} onCreado={() => { setAltaOffline(false); cargar(true) }} />
      )}
    </div>
  )
}

/**
 * Alta de un socio OFFLINE (Martin, 10 sep 2026): cliente + membresía + importe
 * pactado + fecha del próximo ciclo. Desde ahí, cada mes en esa fecha el
 * sistema arma la NV en borrador (membresía + vinos del mes) SIN cobro: el
 * vendedor cobra por fuera, registra el pago y procesa.
 */
function AltaOfflineModal({ niveles, precios, onClose, onCreado }) {
  const [cliente, setCliente] = useState(null)
  const [nivel, setNivel] = useState('')
  const [fecha, setFecha] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [err, setErr] = useState(null)
  const listo = cliente && nivel && fecha
  // El importe NO se carga: es el valor de la membresía (precio del nivel,
  // se edita en Niveles y precios). Acá solo se muestra el vigente.
  const precioNivel = (precios || []).find(n => n.nivel === nivel)?.precio

  const crear = async () => {
    setOcupado(true); setErr(null)
    try {
      await wineClubApi.altaOffline({ cliente_id: cliente.id, nivel, proximo_cobro: fecha })
      onCreado()
    } catch (e) {
      setErr(e.response?.data?.message || 'No se pudo dar el alta.')
      setOcupado(false)
    }
  }

  return (
    <Modal title="Alta de socio offline" onClose={onClose} maxWidth="560px">
      <p style={st.ayuda}>
        Para miembros que pagan <strong>por fuera</strong> (efectivo / transferencia con su vendedor).
        En la fecha cargada —y cada mes desde ahí— el sistema arma la NV del mes en borrador (membresía + vinos al 100%),
        <strong> sin cobro</strong>: el vendedor lo registra al procesar.
      </p>
      <div style={{ display: 'grid', gap: space.md, marginTop: space.lg }}>
        <div>
          <label style={st.datoL}>Cliente</label>
          <BuscadorCliente value={cliente} onChange={setCliente} />
        </div>
        <div>
          <label style={st.datoL}>Membresía</label>
          <select value={nivel} onChange={e => setNivel(e.target.value)} style={{ ...st.select, width: '100%' }}>
            <option value="">Elegí el nivel…</option>
            {niveles.map(n => <option key={n.nivel} value={n.nivel}>{n.nombre}</option>)}
          </select>
        </div>
        <div>
          <label style={st.datoL}>Próximo ciclo</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={{ ...st.select, width: '100%' }} />
        </div>
        {nivel && (
          <p style={{ ...st.ayuda, margin: 0 }}>
            Importe: <strong>{precioNivel != null ? `${fmtMonto(precioNivel)}/mes` : 'el precio vigente del nivel'}</strong> — es el valor de la membresía (se cambia en Niveles y precios, arriba).
          </p>
        )}
      </div>
      {err && <p style={{ ...st.ayuda, color: colors.err, fontWeight: 600 }}>{err}</p>}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: space.md, marginTop: space.lg }}>
        <Boton variante="ghost" onClick={onClose}>Cancelar</Boton>
        <Boton variante="primary" disabled={!listo || ocupado} onClick={crear}>{ocupado ? '…' : 'Dar de alta'}</Boton>
      </div>
    </Modal>
  )
}

function Kpi({ n, label }) {
  return (
    <div style={st.kpi}>
      <span style={{ ...st.kpiN, ...sx.mono }}>{n}</span>
      <span style={st.kpiL}>{label}</span>
    </div>
  )
}

function EstadoMembresia({ estado }) {
  const b = ESTADO_BADGE[estado] || { label: estado, bg: colors.bgSoft, color: colors.textSec }
  return <span style={{ ...sx.badge, background: b.bg, color: b.color }}>{b.label}</span>
}

/**
 * Por dónde cobra la membresía (Martin, 12 sep 2026: "una columna con la marca
 * que tiene la membresía: mobbex, mercadopago, offline"). Es el
 * `mobbex_suscriptores.proveedor`: la pasarela que informa cada cobro, u
 * OFFLINE cuando el calendario lo lleva LGR y cobra el vendedor.
 */
const PASARELAS = {
  mobbex:  { label: 'MOBBEX',       bg: '#e0e7ff', fg: '#3730a3', title: 'Suscripción en Mobbex: cada cobro lo informa Mobbex' },
  mp:      { label: 'MERCADO PAGO', bg: '#dbeafe', fg: '#1e40af', title: 'Suscripción en Mercado Pago: cada cobro lo informa MP' },
  offline: { label: 'OFFLINE',      bg: colors.bgSoft, fg: colors.textSec, title: 'Paga por fuera de toda pasarela: LGR arma la NV mensual y el vendedor registra el cobro' },
  manual:  { label: 'A PEDIDO',     bg: '#fef3c7', fg: '#92400e', title: 'Se anotó sin tarjeta ni pago: cada mes nace la NV con la selección y Ventas lo contacta; paga solo el mes que quiere recibir' },
}

function Pasarela({ proveedor }) {
  const p = PASARELAS[proveedor] || { label: (proveedor || '—').toUpperCase(), bg: colors.bgSoft, fg: colors.textSec, title: proveedor }
  return <span style={{ ...sx.badge, background: p.bg, color: p.fg, whiteSpace: 'nowrap' }} title={p.title}>{p.label}</span>
}

function Tarjeta({ s }) {
  if (s.proveedor === 'offline') {
    return <span style={{ ...sx.badge, background: colors.bgSoft, color: colors.textSec }} title="Paga por fuera de toda pasarela — la NV mensual la genera LGR en la fecha pactada">OFFLINE</span>
  }
  if (s.proveedor === 'manual') {
    return <span style={{ ...sx.badge, background: '#fef3c7', color: '#92400e' }} title="A pedido: sin tarjeta — Ventas lo contacta cada mes con la NV que genera LGR">A PEDIDO</span>
  }
  if (!s.tarjeta) return <span style={{ color: colors.textMeta }}>—</span>
  return (
    <span style={{ ...sx.mono, color: s.tarjeta_vencida ? colors.err : s.tarjeta_no_llega ? colors.warnStrong : colors.text }} title={s.tarjeta_vence ? `Vence ${s.tarjeta_vence}${s.tarjeta_aviso_at ? ` · aviso enviado ${fmtFecha(s.tarjeta_aviso_at)}` : ''}` : undefined}>
      {s.tarjeta.replace(/^Tarjeta\s*/i, '')}{s.tarjeta_vencida ? ' · vencida' : s.tarjeta_no_llega ? ' · no llega al cobro' : ''}
    </span>
  )
}

// La acción principal del socio según su estado (solo dentro del modal).
function AccionSocio({ s, onCambio, sm = true }) {
  const [ocupado, setOcupado] = useState(false)
  const [err, setErr] = useState(null)

  const correr = async (fn, confirmacion) => {
    if (confirmacion && !window.confirm(confirmacion)) return
    setOcupado(true); setErr(null)
    try {
      const { data } = await fn()
      onCambio(data.socio)
    } catch (e) {
      setErr(e.response?.data?.error || 'No se pudo')
    } finally { setOcupado(false) }
  }

  let boton = <span style={st.chipGris}>—</span>
  if (s.estado === 'active') {
    boton = <Boton variante="secondary" sm={sm} disabled={ocupado} onClick={() => correr(() => wineClubApi.pausar(s.id), `¿Pausar la membresía de ${s.nombre}? Deja de cobrarse hasta que se reactive.`)}>{ocupado ? '…' : '⏸ Pausar'}</Boton>
  } else if (s.reactivable) {
    boton = <Boton variante="success" sm={sm} disabled={ocupado} onClick={() => correr(() => wineClubApi.reactivar(s.id), s.proveedor === 'offline' ? `¿Reactivar la membresía offline de ${s.nombre}? Se retoma la generación mensual de la NV (hacia adelante, sin facturar los meses pausados).` : `¿Reactivar la membresía de ${s.nombre}? Mobbex vuelve a cobrar con ${s.tarjeta}.`)}>{ocupado ? '…' : '▶ Reactivar'}</Boton>
  } else if (['suspended', 'cancelled'].includes(s.estado)) {
    boton = <span style={st.chipGris} title={s.tarjeta_vencida ? 'Tarjeta vencida: tiene que suscribirse de nuevo' : 'Sin tarjeta guardada: tiene que suscribirse de nuevo'}>{s.tarjeta_vencida ? 'Tarjeta vencida' : 'Sin tarjeta'}</span>
  }

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.md }}>
      {boton}
      {err && <span style={st.errInline}>{err}</span>}
    </span>
  )
}

function FilaSocio({ s, onAbrir, onCambio }) {
  const [hover, setHover] = useState(false)
  return (
    <tr
      style={{ ...st.tr, background: hover ? '#f8fafc' : undefined, cursor: 'pointer' }}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      onClick={onAbrir} title="Ver y gestionar la membresía"
    >
      <td style={sx.td}>
        <div style={st.nombre}>{s.nombre || <span style={{ color: colors.textMeta }}>sin nombre</span>}</div>
        <div style={st.sub}>{s.email}</div>
      </td>
      <td style={sx.td}><span style={{ ...sx.badge, background: colors.bgSoft, color: colors.textSec }}>{s.nivel_nombre}</span></td>
      <td style={sx.td}><Pasarela proveedor={s.proveedor} /></td>
      <td style={sx.td}>
        <EstadoMembresia estado={s.estado} />
        {/* Cuándo se pausó. Mobbex NO informa esa fecha: solo la tenemos de los
            que LGR vio cambiar de estado (estado_cambiado_at). Si no la hay se
            muestra vacía — el último cobro está en su propia columna y no se
            usa para simular una fecha de pausa que no conocemos. */}
        {s.estado !== 'active' && s.estado_cambiado_at && (
          <div style={st.sub}>pausado {fmtFecha(s.estado_cambiado_at)}</div>
        )}
      </td>
      <td style={sx.td}><Tarjeta s={s} /></td>
      <td style={{ ...sx.td, ...sx.mono }}>{s.ciclos == null ? <span style={{ color: colors.textMeta }}>—</span> : s.ciclos}</td>
      <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>{s.ultimo_cobro ? fmtFecha(s.ultimo_cobro) : <span style={{ color: colors.textMeta }}>—</span>}</td>
      <td style={{ ...sx.td, ...sx.mono }}>{s.estado === 'active' && s.proximo_cobro ? fmtFecha(s.proximo_cobro) : <span style={{ color: colors.textMeta }}>—</span>}</td>
      <td style={{ ...sx.td, ...sx.mono, color: colors.textSec }}>{s.alta ? fmtFecha(s.alta) : '—'}</td>
    </tr>
  )
}

function SocioModal({ socio: s, niveles, onClose, onCambio, navigate }) {
  const [nivelNuevo, setNivelNuevo] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [msg, setMsg] = useState(null)
  const otros = niveles.filter(n => n.nivel !== s.nivel)
  const elegido = otros.find(n => n.nivel === nivelNuevo)

  const cambiar = async () => {
    if (!elegido) return
    if (!window.confirm(`¿Pasar a ${s.nombre} de ${s.nivel_nombre} a ${elegido.nombre}? El precio nuevo rige desde su próximo cobro; la fecha no cambia.`)) return
    setOcupado(true); setMsg(null)
    try {
      const { data } = await wineClubApi.cambiarNivel(s.id, elegido.nivel)
      setMsg({ ok: true, texto: data.mensaje })
      setNivelNuevo('')
      onCambio({ ...data.socio, nivel_nombre: elegido.nombre })
    } catch (e) {
      setMsg({ ok: false, texto: e.response?.data?.error || 'No se pudo cambiar el nivel' })
    } finally { setOcupado(false) }
  }

  return (
    <Modal title={s.nombre || s.email} onClose={onClose} maxWidth="680px">
      <div style={st.ficha}>
        <Dato label="Email" valor={s.email} />
        <Dato label="Teléfono" valor={s.telefono || '—'} />
        <Dato label="Nivel" valor={s.nivel_nombre} />
        <Dato label="Estado" valor={<EstadoMembresia estado={s.estado} />} />
        <Dato label="Tarjeta" valor={<Tarjeta s={s} />} />
        <Dato label="Próximo cobro" valor={s.estado === 'active' && s.proximo_cobro ? fmtFecha(s.proximo_cobro) : '—'} mono />
        <Dato label="Alta" valor={s.alta ? fmtFecha(s.alta) : '—'} mono />
        <Dato label="Ciclos cobrados" valor={s.ciclos == null ? '—' : `${s.ciclos}${s.ultimo_cobro ? ` · último ${fmtFecha(s.ultimo_cobro)}` : ''}`} mono />
        {s.proveedor === 'offline'
          ? <Dato label="Importe (precio del nivel)" valor={s.importe != null ? fmtMonto(s.importe) + '/mes' : '—'} mono />
          : <Dato label="Suscriptor Mobbex" valor={s.suscriptor_uid} mono />}
      </div>

      {s.proveedor === 'offline' && <EditarOffline s={s} onCambio={onCambio} />}

      <div style={st.acciones}>
        <AccionSocio s={s} onCambio={onCambio} sm={false} />
        {s.cliente_id && (
          <Boton variante="secondary" onClick={() => navigate('/clientes/historia', { state: { cliente: { id: s.cliente_id, nombre: s.nombre, codigo_presea: s.codigo_presea } } })}>Historia del cliente</Boton>
        )}
        {s.panel_mobbex && <a href={s.panel_mobbex} target="_blank" rel="noreferrer" style={st.linkSec}>Panel del socio en Mobbex ↗</a>}
      </div>
      {(s.tarjeta_vencida || s.tarjeta_no_llega) && <AvisoTarjeta s={s} onCambio={onCambio} />}

      {s.proveedor !== 'offline' && <CobrosFallidos socio={s} onCambio={() => onCambio(null)} />}

      {['active', 'suspended', 'cancelled'].includes(s.estado) && otros.length > 0 && (
        <div style={st.cambio}>
          <p style={sx.cardTitle}>Cambiar de nivel</p>
          <div style={{ display: 'flex', gap: space.md, marginTop: space.md }}>
            <select value={nivelNuevo} onChange={e => setNivelNuevo(e.target.value)} style={{ ...st.select, flex: 1 }} disabled={ocupado}>
              <option value="">Elegí el nivel nuevo…</option>
              {otros.map(n => <option key={n.nivel} value={n.nivel}>{n.nombre}</option>)}
            </select>
            <Boton variante="primary" disabled={!elegido || ocupado} onClick={cambiar}>{ocupado ? '…' : elegido ? `Pasar a ${elegido.nombre}` : 'Cambiar'}</Boton>
          </div>
          <p style={st.ayuda}>Se mueve el suscriptor en Mobbex: conserva la tarjeta y la fecha de cobro. El precio nuevo rige desde el próximo cobro.{s.estado !== 'active' ? ' Al estar pausada, queda en el nivel nuevo para cuando se reactive.' : ''}</p>
        </div>
      )}

      {msg && <p style={{ ...st.ayuda, color: msg.ok ? colors.ok : colors.err, fontWeight: 600 }}>{msg.texto}</p>}
    </Modal>
  )
}

// Importe pactado y fecha del próximo ciclo de un socio offline: se corrigen acá.
function EditarOffline({ s, onCambio }) {
  const [fecha, setFecha] = useState(s.proximo_cobro || '')
  const [ocupado, setOcupado] = useState(false)
  const [msg, setMsg] = useState(null)

  const guardar = async () => {
    setOcupado(true); setMsg(null)
    try {
      await wineClubApi.editarOffline(s.id, { proximo_cobro: fecha })
      setMsg({ ok: true, texto: 'Guardado.' })
      onCambio({ proximo_cobro: fecha || s.proximo_cobro })
    } catch (e) {
      setMsg({ ok: false, texto: e.response?.data?.message || 'No se pudo guardar' })
    } finally { setOcupado(false) }
  }

  return (
    <div style={st.cambio}>
      <p style={sx.cardTitle}>Membresía offline</p>
      <div style={{ display: 'flex', gap: space.md, marginTop: space.md, alignItems: 'flex-end' }}>
        <div style={{ flex: 1 }}>
          <label style={st.datoL}>Próximo ciclo</label>
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={{ ...st.select, width: '100%' }} />
        </div>
        <Boton variante="primary" disabled={ocupado || !fecha} onClick={guardar}>{ocupado ? '…' : 'Guardar'}</Boton>
      </div>
      <p style={st.ayuda}>En esa fecha (y cada mes desde ahí) el sistema arma la NV del mes en borrador, sin cobro: el vendedor lo registra al procesar. El importe es el valor de la membresía (precio del nivel, en Niveles y precios).</p>
      {msg && <p style={{ ...st.ayuda, color: msg.ok ? colors.ok : colors.err, fontWeight: 600 }}>{msg.texto}</p>}
    </div>
  )
}

// La tarjeta no llega al próximo cobro: mail al socio con el link a su panel de
// Mobbex (donde la cambia). El cron lo manda solo una vez por tarjeta; desde acá
// se puede volver a mandar.
function AvisoTarjeta({ s, onCambio }) {
  const [ocupado, setOcupado] = useState(false)
  const [msg, setMsg] = useState(null)
  const mandar = async () => {
    if (!window.confirm(`¿Mandarle a ${s.email} el mail para cambiar la tarjeta (vence ${s.tarjeta_vence})?`)) return
    setOcupado(true); setMsg(null)
    try {
      const { data } = await wineClubApi.avisarTarjeta(s.id)
      setMsg({ ok: true, texto: data.mensaje }); onCambio(data.socio)
    } catch (e) { setMsg({ ok: false, texto: e.response?.data?.error || 'No se pudo mandar' }) } finally { setOcupado(false) }
  }
  return (
    <div style={{ ...st.cambio, marginTop: space.lg }}>
      <p style={sx.cardTitle}>Tarjeta</p>
      <p style={{ ...st.ayuda, margin: 0, color: s.tarjeta_vencida ? colors.err : colors.warn, fontWeight: 600 }}>
        {s.tarjeta_vencida ? `Tarjeta vencida (${s.tarjeta_vence})` : `La tarjeta vence ${s.tarjeta_vence}, antes del próximo cobro`}
        {s.estado !== 'active' ? ' — no se puede reactivar con ella.' : ': el próximo cobro va a fallar.'}
      </p>
      <p style={st.ayuda}>El socio la cambia desde su panel de Mobbex (“Cambiar tarjeta”). El aviso diario se lo manda una vez por tarjeta{s.tarjeta_aviso_at ? ` — ya se le mandó el ${fmtFecha(s.tarjeta_aviso_at)}` : ''}.</p>
      {s.estado === 'active' && (
        <div style={{ display: 'flex', gap: space.md, alignItems: 'center', marginTop: space.md }}>
          <Boton variante="send" sm disabled={ocupado} onClick={mandar}>{ocupado ? '…' : s.tarjeta_aviso_at ? '✉ Volver a avisar' : '✉ Avisar por mail'}</Boton>
          {msg && <span style={{ fontSize: text.sec, color: msg.ok ? colors.ok : colors.err, fontWeight: 600 }}>{msg.texto}</span>}
        </div>
      )}
    </div>
  )
}

// Los cobros que Mobbex NO pudo hacer.
//
// Regla de Martin (31 ago 2026): LGR reintenta solo lo que falla de ahora en
// adelante. Un período viejo puede haberlo cobrado un vendedor por fuera de
// Mobbex —LGR no tiene cómo verlo— así que insistir solo lo cobraría dos veces.
// Acá una persona decide: reintentar (porque verificó que no se cobró) o cerrar
// diciendo que ya está cobrado.
function CobrosFallidos({ socio, onCambio }) {
  const [cobros, setCobros] = useState(null)
  const [ocupado, setOcupado] = useState(null)   // id de la fila en curso
  const [msg, setMsg] = useState(null)

  const cargar = useCallback(async () => {
    try {
      const { data } = await wineClubApi.cobrosFallidos(socio.id)
      setCobros(data.cobros)
    } catch { setCobros([]) }
  }, [socio.id])
  useEffect(() => { cargar() }, [cargar])

  const correr = async (fila, fn, confirmacion) => {
    if (!window.confirm(confirmacion)) return
    setOcupado(fila.id); setMsg(null)
    try {
      const { data } = await fn()
      setMsg({ ok: true, texto: data.mensaje })
      await cargar()
      onCambio?.()
    } catch (e) {
      setMsg({ ok: false, texto: e.response?.data?.error || 'No se pudo' })
    } finally { setOcupado(null) }
  }

  const reintentar = (f) => correr(f, () => wineClubApi.reintentarCobro(f.id),
    f.resuelto_at
      // Alguien ya afirmó por escrito que este período está cobrado: el confirm
      // tiene que decirlo, no repetir el genérico.
      ? `Este período está CERRADO como cobrado${f.resuelto_por ? ` por ${f.resuelto_por}` : ''} el ${fmtFecha(f.resuelto_at)}${f.resuelto_motivo ? `: "${f.resuelto_motivo}"` : ''}.\n\n¿Reabrirlo y pedirle el cobro a Mobbex igual? Si ese cobro de afuera fue real, le estarías cobrando dos veces.`
      : `¿Cobrarle a ${socio.nombre} el período del ${fmtFecha(f.fecha)} por $${fmtMonto(f.total)}?\n\nAntes de seguir: verificá que ese mes NO se haya cobrado por fuera de Mobbex (transferencia, efectivo, link aparte). LGR no puede verlo, y si ya se cobró le estarías cobrando dos veces.`)

  const cerrar = (f) => correr(f, () => wineClubApi.resolverCobro(f.id, 'Ya se cobró por fuera de Mobbex'),
    `¿Cerrar el período del ${fmtFecha(f.fecha)} por $${fmtMonto(f.total)} sin cobrarlo?\n\nQueda registrado que ya se cobró por fuera de Mobbex y deja de aparecer como pendiente.`)

  if (cobros === null) return null   // la mayoría no tiene fallos: sin placeholder no hay salto de layout
  if (cobros.length === 0) return null

  const pendientes = cobros.filter(c => !c.resuelto_at).length

  return (
    <div style={st.cambio}>
      <p style={sx.cardTitle}>Cobros que fallaron{pendientes > 0 ? ` · ${pendientes} pendiente${pendientes > 1 ? 's' : ''}` : ''}</p>
      <div style={st.cobros}>
        {cobros.map((f, i) => {
          const motivo = MOTIVO_MOBBEX[f.estado_mobbex]
          const badge = ESTADO_COBRO[f.resuelto_at ? 'resuelto' : 'pendiente']
          return (
            <div key={f.id} style={{ ...st.cobro, ...(i === cobros.length - 1 ? { borderBottom: 'none' } : null) }}>
              <span style={{ ...sx.mono, color: colors.text }}>{fmtFecha(f.fecha)}</span>
              <span style={st.cobroImporte}>${fmtMonto(f.total)}</span>
              <span style={{ ...st.cobroEstado, ...(motivo ? null : sx.mono) }} title={`${f.estado_mobbex} · ${f.ejecucion_uid}`}>
                {motivo || f.estado_mobbex}
              </span>
              <span
                style={{ ...sx.badge, background: badge.bg, color: badge.color }}
                title={f.resuelto_at ? `${f.resuelto_motivo || 'Resuelto'} — ${f.resuelto_por || 'LGR'}, ${fmtFecha(f.resuelto_at)}` : undefined}
              >
                {badge.label}
              </span>
              <span style={st.cobroAcciones}>
                {!f.resuelto_at && (
                  <Boton variante="secondary" sm disabled={ocupado === f.id} onClick={() => cerrar(f)}>{ocupado === f.id ? '…' : '✓ Ya cobrado'}</Boton>
                )}
                {/* En una fila ya cerrada esto no es "enviar" sino REABRIR: no
                    puede leerse como el próximo paso lógico. */}
                <Boton variante={f.resuelto_at ? 'secondary' : 'send'} sm disabled={ocupado === f.id} onClick={() => reintentar(f)}>
                  {ocupado === f.id ? '…' : f.resuelto_at ? '↻ Reabrir y cobrar' : '↻ Reintentar'}
                </Boton>
              </span>
              {f.reintentos > 0 && (
                <span style={st.cobroNota} title={f.ultimo_reintento_error || undefined}>
                  {f.reintentos} reintento{f.reintentos > 1 ? 's' : ''} · último {fmtFecha(f.ultimo_reintento_at)} · {f.ultimo_reintento_por || 'automático'}
                  {f.ultimo_reintento_error ? ' · con error' : ''}
                </span>
              )}
            </div>
          )
        })}
      </div>
      <p style={st.ayuda}>
        LGR reintenta solo los cobros que fallan de ahora en adelante, y una vez. Los períodos anteriores no se cobran solos:
        pueden haberse cobrado por fuera de Mobbex. Un reintento es un pedido — el resultado llega como un cobro nuevo.
      </p>
      {msg && <p style={{ ...st.ayuda, color: msg.ok ? colors.ok : colors.err, fontWeight: 600 }}>{msg.texto}</p>}
    </div>
  )
}

function Dato({ label, valor, mono }) {
  return (
    <div style={st.dato}>
      <span style={st.datoL}>{label}</span>
      <span style={{ ...st.datoV, ...(mono ? sx.mono : null) }}>{valor}</span>
    </div>
  )
}

// Precio de cada nivel = el `total` de su suscripción en Mobbex (lo que cobra).
// Se edita acá y se escribe allá; lo que se muestra es lo que Mobbex devolvió.
function PreciosNiveles({ niveles, resumen, onCambio }) {
  const [editando, setEditando] = useState(null) // slug
  const [valor, setValor] = useState('')
  const [ocupado, setOcupado] = useState(false)
  const [err, setErr] = useState(null)

  const guardar = async (n) => {
    const precio = Number(String(valor).replace(/\./g, '').replace(',', '.'))
    if (!precio || precio <= 0) { setErr('Ingresá un precio válido'); return }
    if (!window.confirm(`¿Cambiar el precio de ${n.nombre} de $${fmtMonto(n.precio)} a $${fmtMonto(precio)}? Cambia el precio de la suscripción en Mobbex para todos sus socios. Cuándo lo aplica Mobbex a los cobros ya agendados no está documentado: verificalo en el primer cobro después del cambio.`)) return
    setOcupado(true); setErr(null)
    try {
      await wineClubApi.precio(n.nivel, precio)
      setEditando(null); setValor('')
      onCambio()
    } catch (e) {
      setErr(e.response?.data?.error || 'No se pudo cambiar el precio')
    } finally { setOcupado(false) }
  }

  return (
    <div style={{ ...sx.card, marginBottom: space.xl }}>
      <p style={sx.cardTitle}>Niveles y precios · según Mobbex</p>
      {niveles === null ? (
        <p style={st.ayuda}>Consultando Mobbex…</p>
      ) : (
        <div style={st.niveles}>
          {niveles.map(n => {
            const activos = resumen?.por_nivel?.find(p => p.nivel === n.nivel)?.activos ?? n.activos
            const enEdicion = editando === n.nivel
            return (
              <div key={n.nivel} style={st.nivel}>
                <div style={st.nivelNombre}>{n.nombre}</div>
                <div style={st.nivelSocios}><span style={sx.mono}>{activos}</span> activos</div>
                {n.precio === null ? (
                  <div style={{ ...st.ayuda, color: colors.err }} title={n.mobbex_error || ''}>Sin dato de Mobbex</div>
                ) : enEdicion ? (
                  <div style={{ display: 'flex', gap: space.sm, alignItems: 'center', marginTop: space.sm }}>
                    <span style={{ color: colors.textSec }}>$</span>
                    <input autoFocus value={valor} onChange={e => setValor(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') guardar(n); if (e.key === 'Escape') setEditando(null) }} style={{ ...st.inputPrecio, ...sx.mono }} disabled={ocupado} />
                    <Boton variante="primary" sm disabled={ocupado} onClick={() => guardar(n)}>Guardar</Boton>
                    <Boton variante="ghost" sm disabled={ocupado} onClick={() => { setEditando(null); setErr(null) }}>Cancelar</Boton>
                  </div>
                ) : (
                  <div style={st.precioRow}>
                    <span style={{ ...st.precio, ...sx.mono }}>${fmtMonto(n.precio)}</span>
                    <span style={st.ayuda}>/ mes</span>
                    <Boton variante="secondary" sm onClick={() => { setEditando(n.nivel); setValor(String(Math.round(n.precio))); setErr(null) }}>Editar</Boton>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
      {err && <p style={{ ...st.ayuda, color: colors.err, fontWeight: 600 }}>{err}</p>}
    </div>
  )
}

const st = {
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: space.xl, gap: space.xl },
  title: { margin: 0, fontSize: text.title, fontWeight: 600, color: colors.text },
  subtitle: { margin: '4px 0 0', fontSize: text.body, color: colors.textSec, maxWidth: 640, lineHeight: 1.5 },
  kpis: { display: 'flex', gap: space.xl },
  kpi: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' },
  kpiN: { fontSize: text.display, fontWeight: 600, color: colors.text, lineHeight: 1 },
  kpiL: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', marginTop: 4 },
  toolbar: { display: 'flex', alignItems: 'center', gap: space.lg, padding: `0 0 ${space.lg}px`, borderBottom: `1px solid ${colors.border}`, marginBottom: 0, flexWrap: 'wrap' },
  chips: { display: 'flex', gap: space.sm },
  chip: { padding: '5px 10px', borderRadius: radius.md, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.textSec, fontSize: text.sec, fontWeight: 600, cursor: 'pointer', fontFamily: font.sans },
  chipOn: { background: colors.text, color: colors.bg, borderColor: colors.text },
  select: { padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.sans, background: colors.bg, color: colors.text },
  search: { flex: 1, minWidth: 220, maxWidth: 380, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.sans, outline: 'none' },
  metaTxt: { fontSize: text.meta, color: colors.textSec, marginLeft: 'auto' },
  toolbarFechas: { display: 'flex', alignItems: 'center', gap: space.xl, padding: `${space.lg}px 0`, borderBottom: `1px solid ${colors.border}`, flexWrap: 'wrap' },
  rango: { display: 'flex', alignItems: 'center', gap: space.sm },
  rangoLabel: { fontSize: text.sec, color: colors.textSec, fontWeight: 600, marginRight: space.xs },
  rangoSep: { color: colors.textMeta },
  campoFecha: { width: 110, padding: `${space.md}px ${space.lg}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, fontFamily: font.mono, background: colors.bg, color: colors.text },
  tr: { height: 52, borderBottom: `1px solid ${colors.borderSoft}` },
  nombre: { fontSize: text.body, color: colors.text, fontWeight: 500 },
  sub: { fontSize: text.sec, color: colors.textMeta, marginTop: 2 },
  empty: { padding: `${space.xl}px`, textAlign: 'center', color: colors.textMeta, fontSize: text.body },
  errTxt: { padding: `${space.md}px 0`, fontSize: text.sec, fontWeight: 600, color: colors.err },
  errInline: { fontSize: text.meta, color: colors.err, fontWeight: 600 },
  chipGris: { ...sx.badge, background: colors.bgSoft, color: colors.textMeta },
  ficha: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: `${space.md}px ${space.xl}px` },
  dato: { display: 'flex', flexDirection: 'column', gap: 2 },
  datoL: { fontSize: text.meta, color: colors.textSec, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 },
  datoV: { fontSize: text.body, color: colors.text, overflowWrap: 'anywhere' },
  acciones: { display: 'flex', gap: space.md, marginTop: space.xl, paddingTop: space.lg, borderTop: `1px solid ${colors.border}`, alignItems: 'center', flexWrap: 'wrap' },
  cambio: { marginTop: space.xl, paddingTop: space.lg, borderTop: `1px solid ${colors.border}` },
  linkSec: { fontSize: text.sec, color: colors.link, textDecoration: 'none', marginLeft: 'auto' },
  ayuda: { margin: `${space.md}px 0 0`, fontSize: text.sec, color: colors.textSec, lineHeight: 1.5 },
  // Grid, no flex: con anchos libres el importe arranca donde termina la fecha
  // y las columnas bailan entre filas, que es justo lo que el tabular-nums evita.
  cobros: { display: 'flex', flexDirection: 'column', marginTop: space.md },
  // El 92px del chip es fijo a propósito: cada fila es un grid propio, así
  // que una columna `auto` se mide por fila y PENDIENTE/RESUELTO no alinean.
  cobro: { display: 'grid', gridTemplateColumns: 'auto 110px 1fr 92px auto', alignItems: 'center', columnGap: space.md, rowGap: space.xs, padding: `${space.md}px 0`, borderBottom: `1px solid ${colors.borderSoft}`, fontSize: text.body },
  cobroImporte: { ...sx.mono, fontWeight: 600, color: colors.text, textAlign: 'right' },
  cobroEstado: { fontSize: text.sec, color: colors.textSec },
  cobroAcciones: { display: 'flex', gap: space.sm },
  cobroNota: { gridColumn: '1 / -1', fontSize: text.sec, color: colors.textMeta },
  niveles: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: space.lg, marginTop: space.lg },
  nivel: { padding: space.lg, border: `1px solid ${colors.borderSoft}`, borderRadius: radius.md, background: colors.bgSoft },
  nivelNombre: { fontSize: text.title, fontWeight: 600, color: colors.text },
  nivelSocios: { fontSize: text.sec, color: colors.textSec, marginTop: 2 },
  precioRow: { display: 'flex', alignItems: 'baseline', gap: space.sm, marginTop: space.md, flexWrap: 'wrap' },
  precio: { fontSize: text.title, fontWeight: 600, color: colors.text },
  inputPrecio: { width: 110, padding: `${space.sm}px ${space.md}px`, borderRadius: radius.md, border: `1px solid ${colors.textPlh}`, fontSize: text.body, outline: 'none' },
}
